// Gallery.js
import React, { useState } from 'react';
import './gallery.css';

function Gallery() {
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedImage, setSelectedImage] = useState(null);
  const [visibleCount, setVisibleCount] = useState(8);

  // Gallery data
  const galleryImages = [
    {
      id: 1,
      src: 'https://images.unsplash.com/photo-1551074905-2aa0b986e50a?w=600',
      title: 'Free Eye Screening Camp',
      description: 'Annual eye screening camp at Colombo Community Center',
      category: 'events',
      date: 'March 2024',
      location: 'Colombo 03'
    },
    {
      id: 2,
      src: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=600',
      title: 'Mobile Clinic Outreach',
      description: 'Taking eye care to rural communities',
      category: 'outreach',
      date: 'February 2024',
      location: 'Kalutara'
    },
    {
      id: 3,
      src: 'https://images.unsplash.com/photo-1581091226033-d5c48150dbaa?w=600',
      title: 'Medical Team',
      description: 'Our dedicated specialists at work',
      category: 'team',
      date: 'January 2024',
      location: 'Colombo'
    },
    {
      id: 4,
      src: 'https://images.unsplash.com/photo-1551601651-2a8555f1a136?w=600',
      title: 'Equipment Donation',
      description: 'New diagnostic equipment ceremony',
      category: 'events',
      date: 'December 2023',
      location: 'Headquarters'
    },
    {
      id: 5,
      src: 'https://images.unsplash.com/photo-1584515933487-77982462f5f4?w=600',
      title: 'School Vision Program',
      description: 'Testing vision for students',
      category: 'outreach',
      date: 'November 2023',
      location: 'Colombo 07'
    },
    {
      id: 6,
      src: 'https://images.unsplash.com/photo-1551190822-a9333d879b1f?w=600',
      title: 'Annual Meeting',
      description: 'Club members gathering',
      category: 'events',
      date: 'October 2023',
      location: 'Lions Hall'
    },
    {
      id: 7,
      src: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=600',
      title: 'Volunteer Training',
      description: 'Training new volunteers',
      category: 'team',
      date: 'September 2023',
      location: 'Training Center'
    },
    {
      id: 8,
      src: 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=600',
      title: 'Patient Consultation',
      description: 'Post-surgery follow-up',
      category: 'medical',
      date: 'August 2023',
      location: 'Clinic'
    },
    {
      id: 9,
      src: 'https://images.unsplash.com/photo-1584516150909-c43483ee7932?w=600',
      title: 'Award Ceremony',
      description: 'Recognizing service excellence',
      category: 'events',
      date: 'July 2023',
      location: 'Colombo'
    },
    {
      id: 10,
      src: 'https://images.unsplash.com/photo-1551190822-a9333d879b1f?w=600',
      title: 'Rural Health Camp',
      description: 'Serving remote villages',
      category: 'outreach',
      date: 'June 2023',
      location: 'Galle'
    },
    {
      id: 11,
      src: 'https://images.unsplash.com/photo-1584515933487-77982462f5f4?w=600',
      title: 'Team Meeting',
      description: 'Weekly planning session',
      category: 'team',
      date: 'May 2023',
      location: 'Headquarters'
    },
    {
      id: 12,
      src: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=600',
      title: 'Cataract Surgery Camp',
      description: 'Free surgery for needy patients',
      category: 'medical',
      date: 'April 2023',
      location: 'Base Hospital'
    }
  ];

  const filters = [
    { id: 'all', label: 'All Photos' },
    { id: 'events', label: 'Events' },
    { id: 'outreach', label: 'Outreach' },
    { id: 'medical', label: 'Medical' },
    { id: 'team', label: 'Team' }
  ];

  const filteredImages = activeFilter === 'all' 
    ? galleryImages 
    : galleryImages.filter(img => img.category === activeFilter);

  const displayedImages = filteredImages.slice(0, visibleCount);
  const hasMore = visibleCount < filteredImages.length;

  const handleFilterChange = (filterId) => {
    setActiveFilter(filterId);
    setVisibleCount(8);
  };

  const handleLoadMore = () => {
    setVisibleCount(prev => Math.min(prev + 4, filteredImages.length));
  };

  const handlePrevImage = () => {
    const currentIndex = filteredImages.findIndex(img => img.id === selectedImage.id);
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : filteredImages.length - 1;
    setSelectedImage(filteredImages[prevIndex]);
  };

  const handleNextImage = () => {
    const currentIndex = filteredImages.findIndex(img => img.id === selectedImage.id);
    const nextIndex = currentIndex < filteredImages.length - 1 ? currentIndex + 1 : 0;
    setSelectedImage(filteredImages[nextIndex]);
  };

  return (
    <div className="gallery-page">
      <div className="gallery-container">
        {/* Header */}
        <div className="gallery-header">
          <h1>
            Photo <span>Gallery</span>
          </h1>
          <p>
            Capturing moments of service, care, and community impact
          </p>
        </div>

        {/* Filters */}
        <div className="gallery-filters">
          {filters.map(filter => (
            <button
              key={filter.id}
              className={`filter-btn ${activeFilter === filter.id ? 'active' : ''}`}
              onClick={() => handleFilterChange(filter.id)}
            >
              {filter.label}
            </button>
          ))}
        </div>

        {/* Grid */}
        {displayedImages.length > 0 ? (
          <>
            <div className="gallery-grid">
              {displayedImages.map(image => (
                <div 
                  key={image.id} 
                  className="gallery-item"
                  onClick={() => setSelectedImage(image)}
                >
                  <img src={image.src} alt={image.title} />
                  <div className="overlay">
                    <h3>{image.title}</h3>
                    <p>{image.description}</p>
                    <span className="date">{image.date}</span>
                  </div>
                </div>
              ))}
            </div>

            {hasMore && (
              <div className="load-more">
                <button onClick={handleLoadMore} className="load-more-btn">
                  Load More Photos
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="no-images">
            <p>No images found in this category</p>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {selectedImage && (
        <div className="lightbox" onClick={() => setSelectedImage(null)}>
          <button className="close-btn" onClick={() => setSelectedImage(null)}>×</button>
          <button className="nav-btn prev" onClick={(e) => { e.stopPropagation(); handlePrevImage(); }}>←</button>
          <button className="nav-btn next" onClick={(e) => { e.stopPropagation(); handleNextImage(); }}>→</button>
          
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img src={selectedImage.src} alt={selectedImage.title} />
            <div className="lightbox-details">
              <h2>{selectedImage.title}</h2>
              <p>{selectedImage.description}</p>
              <div className="meta">
                <span>📅 {selectedImage.date}</span>
                <span>📍 {selectedImage.location}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Gallery;